import { forwardRef, useId, type ComponentProps } from "react";

import { cn } from "@/lib/utils";

/**
 * Which loop the mark runs. Mirrors the four design-team assets
 * (`ai-chat-{idle,thinking,speaking,static}.svg`):
 *
 * - `idle`: default launcher / nav mark (slow bob + blink)
 * - `thinking`: a request is in flight (tilt, eyes darting)
 * - `speaking`: a response is streaming (fast bob, mouth flexing)
 * - `static`: no motion; for dense lists and tiny badges
 *
 * Every loop is disabled under `prefers-reduced-motion`.
 */
export type AgentMarkState = "idle" | "thinking" | "speaking" | "static";

type Props = Omit<ComponentProps<"svg">, "children" | "ref"> & {
  state?: AgentMarkState;
  /** Accessible name; omit for a purely decorative mark next to a label. */
  title?: string;
};

// Paths are lifted verbatim from the assets: a 16x13 face on a 20x20 grid,
// offset by (2, 3). Gradient stops read `--rep-start` / `--rep-end` (set for
// light and dark in index.css) with the light-surface brand ramp as fallback.
const EYE_LEFT =
  "M2.5 2C3.05228 2 3.5 2.44772 3.5 3V5.08817C3.5 5.64045 3.05228 6.08817 2.5 6.08817C1.94772 6.08817 1.5 5.64045 1.5 5.08817V3C1.5 2.44772 1.94772 2 2.5 2Z";
const EYE_RIGHT =
  "M13.5 2C14.0523 2 14.5 2.44772 14.5 3V5.08817C14.5 5.64045 14.0523 6.08817 13.5 6.08817C12.9477 6.08817 12.5 5.64045 12.5 5.08817V3C12.5 2.44772 12.9477 2 13.5 2Z";
const NOSE =
  "M8.51912 4.53355C8.90291 4.68936 9.08773 5.12679 8.93193 5.51058L7.84151 8.1966L9.80519 8.5352C10.2134 8.60559 10.4872 8.99355 10.4168 9.40174C10.3465 9.80993 9.9585 10.0838 9.55031 10.0134L6.66882 9.51653C6.44513 9.47796 6.25094 9.34016 6.14069 9.14174C6.03043 8.94333 6.01596 8.70565 6.10135 8.49533L7.54209 4.94636C7.69789 4.56257 8.13533 4.37774 8.51912 4.53355Z";
const MOUTH =
  "M2.32828 11.7203C2.72171 11.3907 3.31322 11.4361 3.64947 11.8218C4.78579 13.1253 6.35673 13.6675 7.99713 13.6626C9.65447 13.6577 11.3213 13.0897 12.5159 12.248C12.9362 11.9519 13.5217 12.0458 13.8238 12.4578C14.1258 12.8698 14.03 13.4438 13.6097 13.74C12.1042 14.8008 10.0583 15.4938 8.00282 15.5C5.93045 15.5061 3.78862 14.8094 2.22474 13.0156C1.8885 12.6299 1.93485 12.05 2.32828 11.7203Z";

/**
 * The agent's face: the animated mark used everywhere an agent is represented
 * (nav items, launchers, chat avatars, greetings). Size it with `className`
 * (e.g. `size-4`); unclassed it is 24px like a Lucide icon, so `[&_svg]` sizing
 * on buttons and menu items applies.
 */
export const AgentMark = forwardRef<SVGSVGElement, Props>(function AgentMark(
  { state = "idle", title, className, ...rest },
  ref
) {
  // Several marks can share a screen; each needs its own gradient id.
  const gradientId = `agent-mark-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const fill = `url(#${gradientId})`;
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 20 20"
      fill="none"
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      data-state={state}
      data-testid="agent-mark"
      {...rest}
      className={cn("agent-mark shrink-0", className)}
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="10"
          y1="3"
          x2="10"
          y2="19"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="var(--rep-start, #201CD1)" />
          <stop offset="100%" stopColor="var(--rep-end, #3E5EFF)" />
        </linearGradient>
      </defs>
      <g className="agent-mark-head">
        <g transform="translate(2, 3)">
          <path
            className="agent-mark-eye"
            fill={fill}
            fillRule="evenodd"
            clipRule="evenodd"
            d={EYE_LEFT}
          />
          <path
            className="agent-mark-eye"
            fill={fill}
            fillRule="evenodd"
            clipRule="evenodd"
            d={EYE_RIGHT}
          />
          <path fill={fill} fillRule="evenodd" clipRule="evenodd" d={NOSE} />
          <path
            className="agent-mark-mouth"
            fill={fill}
            fillRule="evenodd"
            clipRule="evenodd"
            d={MOUTH}
          />
        </g>
      </g>
    </svg>
  );
});
